import { useEffect, useMemo } from 'react';
import { PoolDocumentation, PoolDocumentationType } from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useFormCampaignId from 'hooks/useFormCampaignId';
import useFormPropertyDetailsSelector from 'selectors/useFormPropertyDetailsSelector';
import {
  clearPoolCertificateData,
  errorLoadingPoolCertificate,
  poolCertificateLoaded,
  startLoadingPoolCertificate,
} from 'features/poolCertificate/poolCertificateReducer';
import { updateFormValue } from 'features/form/formReducer';
import { useGetPoolSafetyCertificate } from '@urbanx/agx.pools.client';
import { toCertificateInfo } from './certificateInfoTools';

const PoolSafetyCertificatePreloader = () => {
  const dispatch = useAppDispatch();
  const propertyDetails = useFormPropertyDetailsSelector();
  const campaignId = useFormCampaignId();

  const formPoolDocumentation = useAppSelector(
    (state) => state.form.formValues.PoolDocumentation
  ) as PoolDocumentation;

  const primaryParcel = useMemo(
    () => propertyDetails?.parcels?.find((p) => p.isPrimaryPlan) ?? propertyDetails?.parcels?.[0],
    [propertyDetails]
  );

  const { data, isError, isPending } = useGetPoolSafetyCertificate(
    campaignId!,
    'Queensland',
    primaryParcel?.lotNumber ?? '',
    primaryParcel ? `${primaryParcel.planType}/${primaryParcel.planNumber}` : '',
    !!campaignId &&
      !!primaryParcel?.lotNumber &&
      !!primaryParcel.planType &&
      !!primaryParcel.planNumber
  );

  useEffect(() => {
    dispatch(clearPoolCertificateData());

    return () => {
      dispatch(clearPoolCertificateData());
    };
  }, [dispatch]);

  useEffect(() => {
    // Only update pool documentation when a pool documentation type is selected.
    if (!formPoolDocumentation?.poolDocumentationType) return;

    if (data && !isPending && !isError && data?.success) {
      const poolSafetyCertificate = toCertificateInfo(data.poolSafetyCertificate);
      const buildingCertificate = toCertificateInfo(data.buildingCertificate);

      dispatch(
        poolCertificateLoaded({
          poolSafetyCertificate,
          buildingCertificate,
          poolSafetyCertificateDaysUntilExpiry: data.poolSafetyCertificate?.daysUntilExpiry,
          buildingCertificateDaysUntilExpiry: data.buildingCertificate?.daysUntilExpiry,
        })
      );

      const poolDocumentationType = poolSafetyCertificate
        ? PoolDocumentationType.PoolSafetyCertificate
        : buildingCertificate
          ? PoolDocumentationType.BuildingCertificate
          : PoolDocumentationType.NoPoolCertificate;

      const updatedFormPoolDocumentation = {
        poolDocumentationType: poolDocumentationType,
        poolSafetyCertificate,
        buildingCertificate,
      } satisfies PoolDocumentation;

      dispatch(
        updateFormValue({
          id: 'PoolDocumentation',
          value: { ...updatedFormPoolDocumentation },
        })
      );
    } else if (isPending) {
      dispatch(startLoadingPoolCertificate());
    } else if (isError) {
      dispatch(errorLoadingPoolCertificate());
    }
  }, [isPending, isError, data, dispatch, formPoolDocumentation?.poolDocumentationType]);

  return <></>;
};

export default PoolSafetyCertificatePreloader;
